<template>
    <div>
        <b-modal
            id="m-complete-temuan-inventory"
            title="Approve Temuan ke Inventory"
            size="lg"
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            @show="onShow"
        >
            <p>Barang akan di-approve memakai data final auditor berikut:</p>
            <table class="table table-sm table-bordered">
                <tr v-for="field in fields" :key="field.key">
                    <td width="180" class="font-weight-bold">{{ field.label }}</td>
                    <td>{{ displayField(detail.final_data, field) }}</td>
                </tr>
            </table>
            <template v-if="detail.requires_physical_photos">
                <h6 class="font-weight-bold mb-3">Foto Fisik</h6>
                <b-row>
                    <b-col v-for="slot in physicalPhotos" :key="slot.id" md="3" class="mb-3">
                        <div class="font-weight-bold mb-1">
                            {{ slot.name }} <span v-if="slot.isRequired" class="text-danger">*</span>
                        </div>
                        <div v-if="slot.previewUrl" class="position-relative d-inline-block">
                            <MediaImage :src="slot.previewUrl" class="img-thumbnail media-thumb" />
                            <b-button size="sm" variant="danger" class="position-absolute media-remove" @click="slot.path = slot.previewUrl = null">
                                &times;
                            </b-button>
                        </div>
                        <b-form-file
                            v-else
                            accept="image/*"
                            placeholder="Pilih foto..."
                            :disabled="isUploading"
                            @input="file => onPhysicalFile(slot, file)"
                        />
                    </b-col>
                </b-row>
                <hr />
            </template>
            <b-form-group v-if="isConsignment" label-class="font-weight-bold">
                <template #label>
                    Modal <span class="text-danger">*</span>
                </template>
                <b-input-group prepend="Rp.">
                    <b-input v-model="capital" maxlength="50" @input="sanitizeCapital" />
                </b-input-group>
            </b-form-group>
            <b-form-group
                v-if="isGradeX"
                label-class="font-weight-bold"
                description="Gunakan tanda negatif didepan untuk pengurangan harga (Bila positif, maka dianggap penambahan harga)"
            >
                <template #label>
                    Penyesuaian Harga Grade X <span class="text-danger">*</span>
                </template>
                <b-input-group prepend="Rp.">
                    <b-input v-model="adjGrade" @input="sanitizeAdjGrade" />
                </b-input-group>
            </b-form-group>
            <div class="text-right">
                <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('m-complete-temuan-inventory')">
                    Batal
                </b-button>
                <b-button variant="success" :disabled="isSubmitting || isUploading" @click="submit">
                    {{ isSubmitting ? "Menyimpan..." : "Approve" }}
                </b-button>
            </div>
            <loading-overlay
                :fotoStartTime="uploadTimeStamp.startPresignedUrl"
                :fotoEndTime="uploadTimeStamp.endPresignedUrl"
                :uploadStartTime="uploadTimeStamp.startUploadToS3"
                :uploadEndTime="uploadTimeStamp.endUploadToS3"
                :show="isUploading"
            />
        </b-modal>

        <b-modal
            id="m-barcode-temuan-inventory"
            title="Cetak Barcode"
            centered
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            hide-header-close
        >
            Silahkan cetak barcode dibawah ini. Mohon pastikan barcode tercetak dengan baik sebelum melanjutkan.
            <br /><br />
            <iframe :src="barcodeSrc" height="300px" width="100%" />
            <div class="mt-4 d-flex justify-content-between">
                <b-button v-if="links.linkDamage" variant="dark" @click="showDamage = !showDamage">
                    Print {{ showDamage ? "Barcode" : "Kerusakan" }}
                </b-button>
                <b-button variant="primary" class="ml-auto" @click="finish">Tutup</b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { COMPARE_FIELDS, displayField } from "@/shared/inventory-variance";
import LoadingOverlay from "../barang-masuk/LoadingOverlay.vue";
import MediaImage from "./MediaImage.vue";

const PHYSICAL_UPLOAD_DIR = "invtrypht";

export default {
    name: "ModalCompleteTemuan",
    components: { LoadingOverlay, MediaImage },
    props: {
        detail: { type: Object, required: true }
    },
    data() {
        return {
            fields: COMPARE_FIELDS,
            adjGrade: "",
            capital: "",
            isSubmitting: false,
            isUploading: false,
            physicalPhotos: [],
            uploadTimeStamp: { startPresignedUrl: null, endPresignedUrl: null, startUploadToS3: null, endUploadToS3: null },
            links: { link: "", linkDamage: "" },
            showDamage: false
        };
    },
    computed: {
        isConsignment() {
            return this.detail.variance.origin_type === "CONSIGNMENT";
        },
        isGradeX() {
            return !!this.detail.final_data && this.detail.final_data.grade === "X";
        },
        // A consignment item has no NF yet, so its media is keyed by the consignment code.
        referenceKey() {
            return this.isConsignment ? this.detail.consignment.code : this.detail.variance.no_faktur;
        },
        barcodeSrc() {
            return this.$BASE_PRINT_FILE_PATH + (this.showDamage ? this.links.linkDamage : this.links.link);
        }
    },
    methods: {
        displayField,

        onShow() {
            this.adjGrade = "";
            const { final_data: final, origin_data: origin } = this.detail;
            const capital = final && final.capital != null ? final.capital : origin && origin.capital;
            this.capital = this.isConsignment && capital != null ? this.$helper.formatThousand(Number(capital)) : "";
            this.physicalPhotos = [];
            if (!this.detail.requires_physical_photos) return;

            const existing = (this.detail.sm_media || []).filter(m => m.for === "physical");
            this.$axios
                .get(`master/kategori-foto/selection?kind_id=${this.detail.final_data.item_kind_id}`)
                .then(res => {
                    this.physicalPhotos = res.data.data
                        .filter(c => c.is_video != 1)
                        .map(c => {
                            // The SM's photo of this category stays on the server unless replaced, so it only needs a preview.
                            const old = existing.find(m => m.category_id === c.id);
                            return { id: c.id, name: c.name, isRequired: c.is_required == 1, path: null, previewUrl: old ? old.url : null };
                        });
                })
                .catch(error => this.$helper.parseError(this, error));
        },

        async getPresignedUrl(file, dir) {
            // Every upload starts here, so this also clears the previous upload's steps.
            this.uploadTimeStamp = { startPresignedUrl: this.formatTime(new Date()), endPresignedUrl: null, startUploadToS3: null, endUploadToS3: null };
            const res = await this.$axios.post("get-presigned-url", {
                file_name: file.name,
                file_type: file.type,
                reference_key: this.referenceKey,
                directory_path: dir
            });
            this.uploadTimeStamp.endPresignedUrl = this.formatTime(new Date());
            return res.data.data;
        },

        async uploadToS3(file, presignedUrl) {
            this.uploadTimeStamp.startUploadToS3 = this.formatTime(new Date());
            const res = await fetch(presignedUrl, {
                method: "PUT",
                body: file,
                headers: { "Content-Type": file.type }
            });
            if (!res.ok) throw new Error("Upload to S3 failed");
            this.uploadTimeStamp.endUploadToS3 = this.formatTime(new Date());
        },

        formatTime(date) {
            return date.toTimeString().slice(0, 8);
        },

        async onPhysicalFile(slot, file) {
            if (!file) return;
            this.isUploading = true;
            try {
                const { presigned_url, file_path } = await this.getPresignedUrl(file, PHYSICAL_UPLOAD_DIR);
                await this.uploadToS3(file, presigned_url);
                slot.path = `${PHYSICAL_UPLOAD_DIR}/${this.referenceKey}/${file_path}`;
                slot.previewUrl = URL.createObjectURL(file);
            } catch (error) {
                this.$helper.toastError(this, "Gagal mengupload foto fisik");
            } finally {
                this.isUploading = false;
            }
        },

        sanitizeAdjGrade() {
            const cleaned = String(this.adjGrade).replace(/\./g, "");
            this.adjGrade = cleaned.replace(/(?!^-)[^0-9]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        },

        sanitizeCapital() {
            const digits = String(this.capital).replace(/[^0-9]/g, "");
            this.capital = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        },

        async submit() {
            if (this.isConsignment && this.$helper.isBlank(this.capital)) {
                return this.$helper.toastError(this, "Modal wajib diisi");
            }
            if (this.isGradeX && this.$helper.isBlank(this.adjGrade)) {
                return this.$helper.toastError(this, "Mohon mengisi penyesuaian harga grade untuk Grade X!");
            }
            const missing = this.detail.requires_physical_photos ? this.physicalPhotos.filter(s => s.isRequired && !s.previewUrl).map(s => s.name) : [];
            if (missing.length) return this.$helper.toastError(this, `Foto fisik wajib diisi: ${missing.join(", ")}`);
            if (!confirm("Approve barang ke inventory dengan data final ini?")) return;

            this.isSubmitting = true;
            try {
                const payload = this.isGradeX ? { adjGrade: this.$helper.toNumber(this.adjGrade) } : {};
                if (this.isConsignment) payload.capital = this.$helper.toNumber(this.capital);
                if (this.detail.requires_physical_photos) {
                    payload.physical_photos = this.physicalPhotos.filter(s => s.path).map(s => ({ category_id: s.id, path: s.path }));
                }
                const res = await this.$axios.post(`inventory-variances/${this.detail.variance.id}/complete`, payload);
                this.links = res.data.data;
                this.showDamage = false;
                this.$helper.toastSuccess(this, "Barang berhasil di-approve");
                this.$bvModal.hide("m-complete-temuan-inventory");
                this.$bvModal.show("m-barcode-temuan-inventory");
            } catch (error) {
                this.$helper.parseError(this, error);
            } finally {
                this.isSubmitting = false;
            }
        },

        finish() {
            this.$bvModal.hide("m-barcode-temuan-inventory");
            this.$emit("completed");
        }
    }
};
</script>

<style scoped>
.media-thumb {
    width: 100px;
    height: 100px;
    object-fit: cover;
}

.media-remove {
    top: 0;
    right: 0;
    padding: 0 4px;
}
</style>
